import type { Keystroke } from './typing.js';

export interface Hint {
  id: string;
  title: string;
  body: string;
  /** Higher = more impactful; used to pick the top few. */
  score: number;
}

/** Shown during the countdown. Rotates. */
export const GENERAL_TIPS: string[] = [
  'Rest your fingers on the home row: A S D F for the left hand, J K L ; for the right.',
  'Look at the screen, not the keyboard. Trust your fingers to find the keys.',
  'Read a few words ahead of what you are typing so your hands never wait for your eyes.',
  'Use the opposite-hand Shift key for capitals: right Shift for A, left Shift for L.',
  'Accuracy first. Every typo costs more time than typing a little slower would.',
  'Keep a steady rhythm. Bursts followed by pauses are slower than an even pace.',
  'Hit Space with your thumb and keep the other fingers on the home row.',
  'Sit up straight with wrists floating just above the keyboard, not resting on the desk.',
  'Use the finger closest to each key. Reaching with the wrong finger drags the whole hand.',
  'Type the word you see, not the word you expect. Read punctuation as part of the word.',
];

const RIGHT_PINKY = new Set(['p', ';', "'", '[', ']', '-', '=', '/', 'P', ':', '"']);
const LEFT_PINKY = new Set(['q', 'a', 'z', 'Q', 'A', 'Z', '`', '1', '!']);
const HAND: Record<string, 'L' | 'R'> = {};
for (const c of 'qwertasdfgzxcvb12345') HAND[c] = 'L';
for (const c of 'yuiophjklnm67890;,./') HAND[c] = 'R';

function describeKey(k: string): string {
  if (k === ' ') return 'Space';
  if (k === "'") return "the apostrophe (')";
  if (k === ',') return 'the comma';
  if (k === '.') return 'the full stop';
  return `"${k}"`;
}

/**
 * Turn a race's keystroke log into up to `max` personalised hints, best first.
 * Pure and deterministic so it can be unit tested and run on either side.
 */
export function analyseKeystrokes(keystrokes: Keystroke[], max = 3): Hint[] {
  const hints: Hint[] = [];
  const chars = keystrokes.filter((k) => k.typed.length === 1);
  if (chars.length < 20) return hints;

  // --- Inter-key latencies for correct keystrokes, keyed by the expected char.
  const latency = new Map<string, number[]>();
  const allLatencies: number[] = [];
  for (let i = 1; i < chars.length; i++) {
    const cur = chars[i]!;
    const prev = chars[i - 1]!;
    if (!cur.correct) continue;
    const dt = cur.t - prev.t;
    if (dt <= 0 || dt > 3000) continue; // ignore pauses
    allLatencies.push(dt);
    const key = cur.expected;
    const arr = latency.get(key) ?? [];
    arr.push(dt);
    latency.set(key, arr);
  }
  const median = (arr: number[]) => {
    const s = [...arr].sort((a, b) => a - b);
    return s.length ? s[Math.floor(s.length / 2)]! : 0;
  };
  const overall = median(allLatencies);

  // --- Errors by expected char and substitution pairs.
  const errorsByKey = new Map<string, number>();
  const substitutions = new Map<string, number>();
  let errors = 0;
  for (const k of chars) {
    if (k.correct) continue;
    errors++;
    errorsByKey.set(k.expected, (errorsByKey.get(k.expected) ?? 0) + 1);
    if (k.expected && k.typed !== k.expected) {
      const pair = `${k.expected}→${k.typed}`;
      substitutions.set(pair, (substitutions.get(pair) ?? 0) + 1);
    }
  }
  const accuracy = 1 - errors / chars.length;

  // Rule: slowest keys (needs enough samples).
  const slow = [...latency.entries()]
    .filter(([, arr]) => arr.length >= 4)
    .map(([key, arr]) => ({ key, med: median(arr), n: arr.length }))
    .filter((x) => x.med > overall * 1.6)
    .sort((a, b) => b.med - a.med)
    .slice(0, 2);
  if (slow.length && overall > 0) {
    const names = slow.map((s) => describeKey(s.key)).join(' and ');
    const ratio = Math.round((slow[0]!.med / overall) * 10) / 10;
    const pinky = slow.some((s) => RIGHT_PINKY.has(s.key) || LEFT_PINKY.has(s.key));
    hints.push({
      id: 'slow-keys',
      title: `You hesitate on ${names}`,
      body:
        `Those keys took about ${ratio}x longer than your average keystroke. ` +
        (pinky
          ? 'They are pinky keys: practise reaching with the pinky while the rest of the hand stays anchored on the home row.'
          : 'Drill short words that use them until the reach feels automatic.'),
      score: 50 + (ratio - 1) * 20,
    });
  }

  // Rule: most mistyped key / substitution pattern.
  const worst = [...errorsByKey.entries()].sort((a, b) => b[1] - a[1])[0];
  if (worst && worst[1] >= 2) {
    const [key, count] = worst;
    const sub = [...substitutions.entries()]
      .filter(([pair]) => pair.startsWith(`${key}→`))
      .sort((a, b) => b[1] - a[1])[0];
    let body = `You mistyped ${describeKey(key)} ${count} times.`;
    if (sub && sub[1] >= 2) {
      const typed = sub[0].split('→')[1]!;
      const sameHand = HAND[key.toLowerCase()] && HAND[key.toLowerCase()] === HAND[typed.toLowerCase()];
      body +=
        ` You usually hit ${describeKey(typed)} instead` +
        (sameHand
          ? ', a neighbouring key on the same hand. Your hand is drifting; re-centre on the home row after each word.'
          : '. That is the other hand jumping in early. Slow down slightly on that letter to let the correct hand lead.');
    }
    hints.push({ id: 'mistyped-key', title: `Watch ${describeKey(key)}`, body, score: 40 + count * 8 });
  }

  // Rule: capitals slow you down.
  const capitalLat = [...latency.entries()]
    .filter(([k]) => /[A-Z]/.test(k))
    .flatMap(([, arr]) => arr);
  if (capitalLat.length >= 3 && overall > 0) {
    const m = median(capitalLat);
    if (m > overall * 1.8) {
      hints.push({
        id: 'capitals',
        title: 'Capital letters are costing you',
        body: `Capitals took ${Math.round((m / overall) * 10) / 10}x longer than other keys. Hold Shift with the hand that is not typing the letter, so the typing hand never leaves the home row.`,
        score: 45,
      });
    }
  }

  // Rule: word-boundary pauses.
  const spaceLat = latency.get(' ');
  if (spaceLat && spaceLat.length >= 5 && overall > 0) {
    const m = median(spaceLat);
    if (m > overall * 1.7) {
      hints.push({
        id: 'space-pause',
        title: 'You pause at the end of every word',
        body: 'Space took much longer than other keys. Keep the rhythm going through word boundaries by reading the next word while finishing the current one.',
        score: 42,
      });
    }
  }

  // Rule: too many backspaces / low accuracy.
  const backspaces = keystrokes.filter((k) => k.typed === 'Backspace').length;
  if (accuracy < 0.94 || backspaces > chars.length * 0.08) {
    hints.push({
      id: 'accuracy',
      title: 'Slow down a touch',
      body: `Your accuracy was ${Math.round(accuracy * 100)}%. Every typo stalls the car and needs a Backspace, so typing 10% slower with no mistakes is faster overall.`,
      score: 60 - accuracy * 40,
    });
  }

  // Rule: long pause at sentence start (reading before typing).
  const starts: number[] = [];
  for (let i = 1; i < chars.length; i++) {
    const cur = chars[i]!;
    const prev = chars[i - 1]!;
    if (cur.sentence !== prev.sentence) starts.push(cur.t - prev.t);
  }
  if (starts.length >= 2 && overall > 0 && median(starts) > Math.max(1500, overall * 8)) {
    hints.push({
      id: 'sentence-start',
      title: 'Start typing sooner',
      body: `You took about ${Math.round(median(starts) / 100) / 10}s to begin each new sentence. Read only the first few words, start typing, and read ahead as you go.`,
      score: 38,
    });
  }

  // Rule: uneven rhythm.
  if (allLatencies.length >= 30 && overall > 0) {
    const mean = allLatencies.reduce((a, b) => a + b, 0) / allLatencies.length;
    const variance = allLatencies.reduce((a, b) => a + (b - mean) ** 2, 0) / allLatencies.length;
    const cv = Math.sqrt(variance) / mean;
    if (cv > 0.9) {
      hints.push({
        id: 'rhythm',
        title: 'Aim for an even rhythm',
        body: 'Your key timing is very bursty: fast runs then long pauses. A steady beat, even a slightly slower one, produces a higher WPM.',
        score: 30,
      });
    }
  }

  // Positive reinforcement when nothing stands out.
  if (hints.length === 0) {
    hints.push({
      id: 'great',
      title: 'Clean run',
      body: 'No weak spots stood out this time. Push the pace a little: try reading further ahead of your cursor.',
      score: 1,
    });
  }

  return hints.sort((a, b) => b.score - a.score).slice(0, max);
}

export interface KeyStat {
  key: string;
  count: number;
  errors: number;
  medianMs: number;
}

/** Per-key speed and error counts for a race, for long-term progress tracking. */
export function keyStatsOf(keystrokes: Keystroke[]): KeyStat[] {
  const chars = keystrokes.filter((k) => k.typed.length === 1);
  const byKey = new Map<string, { count: number; errors: number; lat: number[] }>();
  for (let i = 0; i < chars.length; i++) {
    const k = chars[i]!;
    if (!k.expected) continue;
    const e = byKey.get(k.expected) ?? { count: 0, errors: 0, lat: [] };
    e.count++;
    if (!k.correct) e.errors++;
    else if (i > 0) {
      const dt = k.t - chars[i - 1]!.t;
      if (dt > 0 && dt <= 3000) e.lat.push(dt);
    }
    byKey.set(k.expected, e);
  }
  return [...byKey.entries()].map(([key, e]) => {
    const sorted = [...e.lat].sort((a, b) => a - b);
    return { key, count: e.count, errors: e.errors, medianMs: sorted.length ? Math.round(sorted[Math.floor(sorted.length / 2)]!) : 0 };
  });
}
